import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Editor } from '@tiptap/core'
import { nanoid } from 'nanoid'

import { usePrevious } from '@bettermode/common/hooks'
import {
  ChatParticipant,
  Embed,
  Message,
} from '@tribeplatform/gql-client/types'
import { useEditMessage } from '@tribeplatform/react-sdk/hooks'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useFirstRenderContext } from '../../common/ssr/FirstRenderProvider.js'
import { useI18n } from '../../i18n/index.js'
import { useUploadQueue } from '../../Providers/UploadQueueProvider.js'
import { isMessageEdited } from '../Chat/utils/chat.utils.js'
import { ChatAttachment, DraftMessage, DraftMessageType } from '../types.js'

import {
  getAttachmentsToSend,
  isAlreadyEditingMessage,
  isGiphyMessage,
  messageToDraftMessage,
} from './chat.context.utils.js'
import { useDraftChats } from './DraftChatsProvider.js'
import {
  SendMessageToExistingChatParams,
  SendMessageToNewChatParams,
} from './types.js'
import { useHandleChatDrafts } from './useHandleChatDrafts.js'
import { useSendMessage } from './useSendMessage.js'
import { useUploadAttachments } from './useUploadAttachments.js'

const STORAGE_KEY = 'SHOW_CHAT_DETAILS'
const MAX_ATTACHMENTS_LIMIT = 10

const emptyDraftMessage: DraftMessage = {
  attachments: [],
  embeds: [],
  content: '',
  type: DraftMessageType.New,
}

interface ChatContextType {
  chatId: string
  showChatDetails: boolean
  selectedParticipant: ChatParticipant | undefined
  draftMessage: DraftMessage
  uploadingAttachments: ChatAttachment[]
  isSendingMessage: boolean
  isEditingMessage: boolean
  editorRef: React.RefObject<Editor>
  setSelectedParticipant: (participant?: ChatParticipant) => void
  toggleShowChatDetails: (participant?: ChatParticipant) => void
  addAttachments: (attachments: File[], type: ChatAttachment['type']) => void
  removeAttachments: (attachments: ChatAttachment[]) => void
  addEmbeds: (embeds: Embed[]) => void
  removeEmbeds: (embeds: Embed[]) => void
  updateDraftContent: (content: string) => void
  sendMessageToNewChat: (params: SendMessageToNewChatParams) => Promise<void>
  sendMessageToExistingChat: (
    params: SendMessageToExistingChatParams,
  ) => Promise<void>
  setMessageToEdit: (message: Message) => Promise<boolean>
  cancelEditMessage: () => void
  setEditorRef: (editor: Editor) => void
}

const ChatContext = createContext<ChatContextType>({} as ChatContextType)

interface Props {
  children: ReactNode
  chatId: string
}

export const ChatContextProvider = ({ children, chatId }: Props) => {
  const { isFirstRender } = useFirstRenderContext()
  const { $t } = useI18n()
  const getShowChatDetails = useCallback(() => {
    if (isFirstRender) {
      return false
    }
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'false')
  }, [isFirstRender])
  const setShowChatDetailsStorage = useCallback((value: boolean) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  }, [])
  const { getChatDraft, updateChatDraft, draftsLoaded } = useDraftChats()
  const [showChatDetails, setShowChatDetails] = useState(getShowChatDetails())
  const [selectedParticipant, setSelectedParticipant] = useState<
    ChatParticipant | undefined
  >()
  const [draftMessage, setDraftMessage] =
    useState<DraftMessage>(emptyDraftMessage)
  const [uploadingAttachments, setUploadingAttachments] = useState<
    ChatAttachment[]
  >([])
  const { addToUploadQueue, removeFromUploadQueue } = useUploadQueue()
  const { uploadAttachments } = useUploadAttachments()
  const { mutateAsync: editMessage } = useEditMessage()
  const editorRef = useRef<Editor | null>(null)
  const setEditorRef = useCallback((editor: Editor) => {
    editorRef.current = editor
  }, [])

  const { handleSendMessageToExistingChat, handleSendMessageToNewChat } =
    useSendMessage({
      chatId,
    })
  const { handleUpdateDraftContent, saveChatDraft } = useHandleChatDrafts({
    chatId,
  })

  /**
   * To avoid re-rendering as user types, the draft content is updated
   * separately in a debounced manner.
   */
  const updateDraftContent = useCallback(
    (content: string) => {
      if (draftMessage.type === DraftMessageType.Edit) {
        return
      }

      handleUpdateDraftContent(content, draftMessage)
    },
    [draftMessage, handleUpdateDraftContent],
  )

  const setMessageToEdit = useCallback(
    async (message: Message) => {
      if (isAlreadyEditingMessage(draftMessage, message)) {
        return false
      }

      const editorHasContent = editorRef.current?.getText().trim() !== ''
      const hasDraftMessageChanges =
        editorHasContent ||
        !!draftMessage.attachments.length ||
        !!draftMessage.embeds.length

      if (hasDraftMessageChanges) {
        const confirmed = await confirm({
          title: $t({
            id: 'Messaging.Chat.ConfirmEditMessage',
            defaultMessage: 'Unsent message exists',
          }),
          description: $t({
            id: 'Messaging.Chat.ConfirmEditMessageDescription',
            defaultMessage:
              'You have an unsent draft. Do you want to discard it and edit this message instead?',
          }),
        })

        if (!confirmed) {
          return false
        }
      }

      setDraftMessage(messageToDraftMessage(message))
      editorRef?.current?.commands.focus()

      return true
    },
    [$t, draftMessage],
  )

  const cancelEditMessage = useCallback(() => {
    setDraftMessage(emptyDraftMessage)
  }, [])

  const removeAttachments = useCallback(
    (attachments: ChatAttachment[]) => {
      const ids = attachments.map(att => att.id)
      attachments.forEach(att => removeFromUploadQueue({ id: att.id }))

      const newAttachments = draftMessage.attachments.filter(
        a => !ids.includes(a.id),
      )
      const newDraftMessage: DraftMessage = {
        ...draftMessage,
        attachments: newAttachments,
      }
      setDraftMessage(newDraftMessage)
      saveChatDraft(newDraftMessage)
    },
    [draftMessage, removeFromUploadQueue, saveChatDraft],
  )

  const addAttachments = useCallback(
    async (files: File[], type: ChatAttachment['type']) => {
      const currentLength = draftMessage.attachments.length
      const newLength = currentLength + files.length
      if (newLength > MAX_ATTACHMENTS_LIMIT) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Messaging.MaxAttachmentsLimitReached',
            defaultMessage: 'Message cannot have more than 10 attachments.',
          }),
          status: 'error',
        })
        return
      }

      const newAttachments: ChatAttachment[] = files.map(file => ({
        id: nanoid(),
        file,
        type,
      }))
      newAttachments.forEach(att => addToUploadQueue({ id: att.id }))
      const draftMessageAttachments = [
        ...draftMessage.attachments,
        ...newAttachments,
      ]
      setDraftMessage(prev => ({
        ...prev,
        attachments: draftMessageAttachments,
      }))
      try {
        setUploadingAttachments(newAttachments)
        const uploadedAttachments = await uploadAttachments(newAttachments)
        const newAttachmentsWithUploadedFile = draftMessageAttachments.map(
          att => {
            const uploadedAttachment = uploadedAttachments.find(
              uatt => uatt.id === att.id,
            )

            return uploadedAttachment ?? att
          },
        )
        const newDraftMessage: DraftMessage = {
          ...draftMessage,
          attachments: newAttachmentsWithUploadedFile,
        }
        setDraftMessage(newDraftMessage)
        saveChatDraft(newDraftMessage)
        newAttachments.forEach(att => removeFromUploadQueue({ id: att.id }))
      } catch (error) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Generics.SomethingWentWrongTryAgain',
            defaultMessage: 'Something went wrong, please try again.',
          }),
          status: 'error',
        })
        removeAttachments(newAttachments)
      } finally {
        setUploadingAttachments([])
      }
    },
    [
      $t,
      addToUploadQueue,
      draftMessage,
      removeAttachments,
      removeFromUploadQueue,
      saveChatDraft,
      uploadAttachments,
    ],
  )

  const addEmbeds = useCallback(
    (embeds: Embed[]) => {
      const newEmbeds = embeds.filter(
        embed => !draftMessage.embeds.some(e => e.id === embed.id),
      )
      const draftMessageEmbeds = [...draftMessage.embeds, ...newEmbeds]
      const newDraftMessage: DraftMessage = {
        ...draftMessage,
        embeds: draftMessageEmbeds,
      }
      setDraftMessage(newDraftMessage)
      saveChatDraft(newDraftMessage)
    },
    [draftMessage, saveChatDraft],
  )

  const removeEmbeds = useCallback(
    (embeds: Embed[]) => {
      const ids = embeds.map(e => e.id)
      const newEmbeds = draftMessage.embeds.filter(e => !ids.includes(e.id))
      const newDraftMessage: DraftMessage = {
        ...draftMessage,
        embeds: newEmbeds,
      }
      setDraftMessage(newDraftMessage)
      saveChatDraft(newDraftMessage)
    },
    [draftMessage, saveChatDraft],
  )

  const clearDraftMessage = useCallback(() => {
    setDraftMessage(emptyDraftMessage)
    updateChatDraft(
      {
        attachments: [],
        embeds: [],
        content: '',
      },
      chatId,
    )
    setUploadingAttachments([])
  }, [chatId, updateChatDraft])

  const toggleShowChatDetails = useCallback(
    (participant?: ChatParticipant) => {
      if (participant && showChatDetails) {
        setSelectedParticipant(participant)
        return
      }

      setShowChatDetails(!showChatDetails)
      setShowChatDetailsStorage(!showChatDetails)
      setSelectedParticipant(participant)
    },
    [setShowChatDetailsStorage, showChatDetails],
  )
  const previousChatId = usePrevious(chatId)
  const loadedDraft = useRef(false)

  useEffect(() => {
    if ((!loadedDraft.current || previousChatId !== chatId) && draftsLoaded) {
      setSelectedParticipant(undefined)
      const draftFromStorage = getChatDraft(chatId)
      setDraftMessage({
        ...emptyDraftMessage,
        ...draftFromStorage,
      })
      loadedDraft.current = true
    }
  }, [chatId, previousChatId, draftsLoaded, getChatDraft])
  const isSendingMessage = useRef(false)

  const handleEditMessage = useCallback(
    async (newContent: string) => {
      if (draftMessage.type !== DraftMessageType.Edit) {
        return
      }

      const { originalMessage } = draftMessage
      const { attachmentsIds, imagesIds, attachments, images } =
        getAttachmentsToSend(draftMessage.attachments)
      const { embeds } = draftMessage
      const embedIds = embeds.map(({ id }) => id)

      const messageEdited = isMessageEdited({
        originalMessage,
        newContent,
        newAttachmentIds: [...attachmentsIds, ...imagesIds],
        newEmbedIds: embedIds,
      })

      if (!messageEdited) {
        clearDraftMessage()
        return
      }

      const currentDraftMessage: DraftMessage = {
        ...draftMessage,
      }
      clearDraftMessage()
      try {
        await editMessage({
          chatId,
          originalMessage,
          input: {
            messageId: originalMessage._id,
            pageId: originalMessage.pageId,
            attachmentIds: attachmentsIds,
            imageIds: imagesIds,
            embedIds,
            content: newContent,
            attachments,
            images,
            embeds,
          },
        })
      } catch (error) {
        setDraftMessage(prev => ({
          ...prev,
          ...currentDraftMessage,
        }))
        throw error
      }
    },
    [chatId, clearDraftMessage, draftMessage, editMessage],
  )

  const sendMessageToExistingChat = useCallback(
    async (params: SendMessageToExistingChatParams) => {
      if (isSendingMessage.current) {
        return
      }

      isSendingMessage.current = true
      try {
        if (isGiphyMessage(params)) {
          const giphyAttachments: ChatAttachment[] = params.giphyFiles.map(
            file => ({
              id: nanoid(),
              file,
              type: 'giphy',
            }),
          )
          const uploadedAttachments = await uploadAttachments(giphyAttachments)
          await handleSendMessageToExistingChat({
            content: '<p></p>',
            attachments: uploadedAttachments,
            embeds: [],
            authorParticipantId: params.authorParticipantId,
          })
          return
        }

        const content = editorRef.current?.getHTML()
        const isEmpty =
          !editorRef.current?.getText().trim() &&
          !draftMessage.attachments.length &&
          !draftMessage.embeds.length

        if (isEmpty) {
          return
        }

        if (draftMessage.type === DraftMessageType.Edit) {
          await handleEditMessage(content)
        } else {
          await handleSendMessageToExistingChat({
            content,
            attachments: draftMessage.attachments,
            embeds: draftMessage.embeds,
            authorParticipantId: params.authorParticipantId,
          })
          clearDraftMessage()
          editorRef.current?.commands.setContent('')
        }
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Could not send message',
            id: 'Messaging.MessageFailed.Title',
          }),
          description: $t({
            defaultMessage: 'Something went wrong, please try again.',
            id: 'Generics.SomethingWentWrongTryAgain',
          }),
          status: 'error',
        })
      } finally {
        isSendingMessage.current = false
      }
    },
    [
      draftMessage.type,
      draftMessage.attachments,
      draftMessage.embeds,
      handleSendMessageToExistingChat,
      clearDraftMessage,
      uploadAttachments,
      handleEditMessage,
      $t,
    ],
  )

  const sendMessageToNewChat = useCallback(
    async (params: SendMessageToNewChatParams) => {
      if (isSendingMessage.current) {
        return
      }

      isSendingMessage.current = true
      try {
        if (isGiphyMessage(params)) {
          const giphyAttachments: ChatAttachment[] = params.giphyFiles.map(
            file => ({
              id: nanoid(),
              file,
              type: 'giphy',
            }),
          )
          const uploadedAttachments = await uploadAttachments(giphyAttachments)
          await handleSendMessageToNewChat({
            recipientId: params.recipientId,
            content: '<p></p>',
            attachments: uploadedAttachments,
            embeds: [],
          })
        } else {
          const content = editorRef.current?.getHTML()

          const isEmpty =
            !editorRef.current?.getText().trim() &&
            !draftMessage.attachments.length &&
            !draftMessage.embeds.length

          if (isEmpty) {
            return
          }

          await handleSendMessageToNewChat({
            recipientId: params.recipientId,
            content,
            attachments: draftMessage.attachments,
            embeds: draftMessage.embeds,
          })
          clearDraftMessage()
          editorRef.current?.commands.setContent('')
        }
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Could not send message',
            id: 'Messaging.MessageFailed.Title',
          }),
          description: $t({
            defaultMessage: 'Something went wrong, please try again.',
            id: 'Generics.SomethingWentWrongTryAgain',
          }),
          status: 'error',
        })
      } finally {
        isSendingMessage.current = false
      }
    },
    [
      uploadAttachments,
      handleSendMessageToNewChat,
      draftMessage.attachments,
      draftMessage.embeds,
      clearDraftMessage,
      $t,
    ],
  )

  const value = useMemo(
    () => ({
      chatId,
      showChatDetails,
      selectedParticipant,
      draftMessage,
      setSelectedParticipant,
      toggleShowChatDetails,
      isSendingMessage: isSendingMessage.current,
      isEditingMessage: draftMessage.type === DraftMessageType.Edit,
      addAttachments,
      removeAttachments,
      addEmbeds,
      removeEmbeds,
      uploadingAttachments,
      updateDraftContent,
      sendMessageToExistingChat,
      sendMessageToNewChat,
      setMessageToEdit,
      cancelEditMessage,
      editorRef,
      setEditorRef,
    }),
    [
      chatId,
      showChatDetails,
      selectedParticipant,
      draftMessage,
      setSelectedParticipant,
      toggleShowChatDetails,
      isSendingMessage,
      addAttachments,
      removeAttachments,
      addEmbeds,
      removeEmbeds,
      uploadingAttachments,
      updateDraftContent,
      sendMessageToExistingChat,
      sendMessageToNewChat,
      setMessageToEdit,
      cancelEditMessage,
      setEditorRef,
    ],
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext)

  if (!context) {
    throw new Error('useChatContext must be used within a ChatContextProvider')
  }

  return context
}
